import React, { ReactNode } from "react"
import type { MeUserDataType } from "src/app";
import { EMBEDDED_CHAT_WIDTH } from "src/common/layout-constants"
import { useEmbeddedChatBreakpoint } from "src/components/embedded-chat";
import { EvaluationBanner } from "src/components/EvaluationBanner";
import { LeftNavigation } from "src/components/left-navigation";
import { Toaster } from "src/components/ui/toaster";
import { useEmbeddedChat } from "src/context/EmbeddedChatContext";
import { NavigationProvider, useNavigation } from "src/context/NavigationContext";

import { Footer } from "../footer"
import { Header } from "../header"

export const getNoneLayout = (page: React.ReactElement) => page

function DefaultLayoutContent({ page, me }: { page: React.ReactElement, me: MeUserDataType }) {
  const { isLeftNavCollapsed } = useNavigation();
  const { isOpen: isEmbeddedChatOpen, isDisabled: isEmbeddedChatDisabled } = useEmbeddedChat();
  const breakpoint = useEmbeddedChatBreakpoint();

  // Only apply margin on desktop when chat is open and not disabled
  const shouldApplyMargin = isEmbeddedChatOpen && !isEmbeddedChatDisabled && breakpoint === "desktop";

  return (
    <div className="h-screen flex bg-background overflow-hidden">
      <LeftNavigation isCollapsed={isLeftNavCollapsed} />
      <div
        className="flex flex-col flex-1 min-w-0 h-screen transition-[margin-right] duration-300 ease-in-out"
        style={{ marginRight: shouldApplyMargin ? EMBEDDED_CHAT_WIDTH : 0 }}
      >
        <Header me={me} />
        <EvaluationBanner suspendedAt={me.organization?.suspendedAt ?? null} />
        <main className="flex-1 flex flex-col h-full relative max-h-full overflow-auto custom-scrollbar">
          {page}
        </main>
      </div>
      <Toaster />
    </div>
  );
}

export const getDefaultLayout = (page: React.ReactElement, me: MeUserDataType) => {
  return (
    <NavigationProvider>
      <DefaultLayoutContent page={page} me={me} />
    </NavigationProvider>
  );
}

type SidebarLayoutProps = {
  sidebarContent: ReactNode;
  mainContent: ReactNode;
  me: MeUserDataType;
  showFooter?: boolean;
}

export const SidebarLayout = ({
  sidebarContent,
  mainContent,
  me,
  showFooter = false,
}: SidebarLayoutProps) => {
  const { isOpen: isEmbeddedChatOpen, isDisabled: isEmbeddedChatDisabled } = useEmbeddedChat();
  const breakpoint = useEmbeddedChatBreakpoint();

  // Only apply margin on desktop when chat is open and not disabled
  const shouldApplyMargin = isEmbeddedChatOpen && !isEmbeddedChatDisabled && breakpoint === "desktop";

  return (
    <div className="h-screen flex bg-background overflow-hidden">
      {/* Sidebar - only render if provided */}
      {sidebarContent && sidebarContent}

      <div
        className="flex flex-col flex-1 min-w-0 h-screen transition-[margin-right] duration-300 ease-in-out"
        style={{ marginRight: shouldApplyMargin ? EMBEDDED_CHAT_WIDTH : 0 }}
      >
        <Header me={me} />
        <EvaluationBanner suspendedAt={me.organization?.suspendedAt ?? null} />
        {/* Main content area */}
        <div className="flex-1 flex flex-col h-full relative max-h-full overflow-auto">
          {mainContent}
        </div>
        {showFooter && <Footer />}
      </div>
      <Toaster />
    </div>
  );
};

export const getSidebarLayout = (
  page: React.ReactElement,
  sidebarContent: ReactNode,
  me: MeUserDataType,
  options?: { 
    showFooter?: boolean;
  }
) => {
  return (
    <SidebarLayout
      sidebarContent={sidebarContent}
      mainContent={page}
      me={me}
      showFooter={options?.showFooter}
    />
  );
}
